// «Аналитика», «База законов» и «Логи» кабинета администратора.
import { useEffect, useState } from "react";
import { getJson } from "../api";
import { Icon } from "../components/Icon";
import { KINDS, num, PROVIDER_NAMES, shortDay, StatCard, userTitle, when, type AdminEvent, type Day, type LawCode } from "./shared";

function useData<T>(url: string): [T | null, string] {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState("");
  useEffect(() => {
    setData(null);
    getJson<T>(url).then(setData).catch((failure) => setError((failure as Error).message));
  }, [url]);
  return [data, error];
}

// Столбики по дням: высота — доля от самого большого дня; число — в подсказке и под столбиком у последних дней.
function Bars({ days, parts, title }: { days: Day[]; parts: { key: keyof Day; label: string; tone: string }[]; title: string }) {
  const total = (day: Day) => parts.reduce((sum, part) => sum + (day[part.key] as number), 0);
  const top = Math.max(1, ...days.map(total));
  return (
    <section className="adm-card adm-chart">
      <div className="adm-chart-head">
        <h2>{title}</h2>
        <ul className="adm-legend">{parts.map((part) => <li key={part.key}><span className={`is-${part.tone}`} />{part.label}</li>)}</ul>
      </div>
      <div className="adm-bars" role="img" aria-label={`${title} за 30 дней`}>
        {days.map((day) => (
          <div key={day.day} className="adm-bar" title={`${shortDay(day.day)}: ${parts.map((part) => `${part.label.toLowerCase()} — ${day[part.key]}`).join(", ")}`}>
            <div className="adm-bar-stack" style={{ height: `${(total(day) / top) * 100}%` }}>
              {parts.map((part) => (day[part.key] as number) > 0 && (
                <span key={part.key} className={`is-${part.tone}`} style={{ flexGrow: day[part.key] as number }} />
              ))}
            </div>
            <span className="adm-bar-day">{new Date(day.day).getDate()}</span>
          </div>
        ))}
      </div>
    </section>
  );
}

export function AnalyticsSection() {
  const [days, error] = useData<Day[]>("/api/admin/analytics");
  const sum = (key: keyof Day) => (days || []).reduce((total, day) => total + (day[key] as number), 0);
  return (
    <>
      <div className="adm-title"><h1>Аналитика</h1><span className="adm-muted">за последние 30 дней</span></div>
      {error && <p className="adm-error">{error}</p>}
      {!days ? <p className="adm-muted">Загружаю…</p> : (
        <>
          <ul className="adm-stats">
            <StatCard icon="userplus" tone="green" label="Зарегистрировались" value={num(sum("registered"))} hint="за 30 дней" />
            <StatCard icon="globe" tone="gray" label="Новые гости" value={num(sum("guests"))} hint="новые браузеры за 30 дней" />
            <StatCard icon="spark" tone="amber" label="Сообщения помощнику" value={num(sum("assistant"))} hint="за 30 дней" />
            <StatCard icon="doc" tone="blue" label="Вопросы мастера" value={num(sum("documents"))} hint="уточняющие вопросы ИИ" />
          </ul>
          <Bars days={days} title="Новые пользователи"
            parts={[{ key: "registered", label: "Зарегистрированные", tone: "green" }, { key: "guests", label: "Гости", tone: "blue" }]} />
          <Bars days={days} title="Работа ИИ"
            parts={[{ key: "assistant", label: "Помощник", tone: "amber" }, { key: "documents", label: "Мастер документов", tone: "violet" }]} />
        </>
      )}
    </>
  );
}

export function LawsSection() {
  const [codes, error] = useData<LawCode[]>("/api/admin/laws");
  // Последние строки — постановления Пленума ВС, обзоры практики ВС, решения Конституционного Суда и судов Дагестана:
  // в «Документов» и «Статей» не входят, векторы — общие.
  const judicial = ["practice", "plenum", "overview", "courts"];
  const laws = (codes || []).filter((code) => !judicial.includes(code.kind || ""));
  const articles = laws.reduce((total, code) => total + code.articles, 0);
  const chunks = (codes || []).reduce((total, code) => total + code.chunks, 0);
  const vectors = (codes || []).reduce((total, code) => total + code.vectors, 0);
  // Кодексы сверяет сервер в России раз в неделю (воскресенье): дольше 9 дней без сверки — обновление не приходит.
  // Пленум, обзоры ВС и решения судов загружают вручную — их дата о сервере в России ничего не говорит.
  const newest = (codes || []).filter((code) => code.kind !== "plenum" && code.kind !== "overview" && code.kind !== "courts")
    .reduce((latest, code) => (code.checked_on > latest ? code.checked_on : latest), "");
  const days = newest ? Math.floor((Date.now() - new Date(newest).getTime()) / 86_400_000) : 0;
  const stale = Boolean(newest) && days > 9;
  return (
    <>
      <div className="adm-title"><h1>База законов</h1><span className="adm-muted">с pravo.gov.ru, обновляется раз в неделю</span></div>
      {error && <p className="adm-error">{error}</p>}
      {!codes ? <p className="adm-muted">Загружаю…</p> : (
        <>
          <ul className="adm-stats">
            <StatCard icon="scales" tone="blue" label="Документов" value={num(laws.length)} hint="кодексы, Конституция, законы" />
            <StatCard icon="book" tone="gray" label="Статей" value={num(articles)} hint="и преамбул" />
            <StatCard icon="calendar" tone={stale ? "red" : "gray"} label="Последняя сверка" value={newest ? shortDay(newest) : "—"}
              hint={stale ? `${days} дн. без обновления — проверьте сервер в России` : "обновление по воскресеньям"} />
            <StatCard icon="check" tone="green" label="Поиск по смыслу" value={`${chunks ? Math.floor((vectors / chunks) * 100) : 0} %`}
              hint={`${num(vectors)} из ${num(chunks)} векторов`} share={chunks ? vectors / chunks : 0} />
          </ul>
          <section className="adm-card adm-table-card">
            <table className="adm-table">
              <thead><tr><th>Документ</th><th>Статей</th><th>Векторы</th><th>Сверено</th></tr></thead>
              <tbody>
                {codes.map((code) => (
                  <tr key={code.key}>
                    <td><strong>{code.name}</strong><span className="adm-muted"> · {code.key}</span></td>
                    <td>{num(code.articles)}{code.kind === "practice" || code.kind === "courts" ? <span className="adm-muted"> решений</span> : code.kind === "plenum" ? <span className="adm-muted"> постановлений</span> : code.kind === "overview" ? <span className="adm-muted"> обзоров</span> : null}</td>
                    <td>
                      <span className="adm-progress is-inline"><span style={{ width: `${code.chunks ? (code.vectors / code.chunks) * 100 : 0}%` }} /></span>
                      <span className="adm-muted">{code.chunks ? Math.floor((code.vectors / code.chunks) * 100) : 0} %</span>
                    </td>
                    <td className="adm-muted">{code.checked_on ? shortDay(code.checked_on) : ""} {(code.checked.match(/\(редакция[^)]*\)/) || [""])[0]}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        </>
      )}
    </>
  );
}

const EVENT_KINDS: [string, string][] = [["", "Все"], ["login", "Входы"], ["chat", "Чаты"], ["doc", "Документы"], ["calc", "Расчёты"], ["upload", "Загрузки"]];
const EVENT_ICONS: Record<string, string> = { login: "login", chat: "chat", doc: "doc", calc: "calc", upload: "upload" };

export function LogsSection({ user, onReset }: { user: { id: number; title: string } | null; onReset: () => void }) {
  const [events, error] = useData<AdminEvent[]>(`/api/admin/events${user ? `?user=${user.id}` : ""}`);
  const [kind, setKind] = useState("");
  const shown = (events || []).filter((event) => !kind || event.kind === kind);
  return (
    <>
      <div className="adm-title"><h1>Логи</h1><span className="adm-muted">последние действия — только названия, без содержимого</span></div>
      {user && (
        <p className="adm-card adm-scope">
          <Icon name="user" />Только {user.title}
          <button type="button" className="adm-chip-link" onClick={onReset}><Icon name="close" />Показать всех</button>
        </p>
      )}
      <div className="adm-chips">
        {EVENT_KINDS.map(([value, label]) => (
          <button key={value || "all"} type="button" aria-pressed={kind === value} className={`adm-chip ${kind === value ? "is-active" : ""}`}
            onClick={() => setKind(value)}>{label}</button>
        ))}
      </div>
      {error && <p className="adm-error">{error}</p>}
      {!events ? <p className="adm-muted">Загружаю…</p> : shown.length ? (
        <ul className="adm-card adm-log">
          {shown.map((event, i) => (
            <li key={i}>
              <span className={`adm-row-icon is-${event.kind === "login" ? "green" : event.kind === "chat" ? "violet" : event.kind === "calc" ? "amber" : "blue"}`}>
                <Icon name={EVENT_ICONS[event.kind] || "doc"} />
              </span>
              <span className="adm-log-body">
                <span className="adm-log-title">
                  <span className="adm-pill is-gray">{KINDS[event.kind] || event.kind}</span>
                  {event.kind === "login" ? `Вход через ${PROVIDER_NAMES[event.title] || event.title}` : event.title}
                </span>
                <span className="adm-muted">{userTitle(event)}{event.user_kind === "guest" ? " (гость)" : ""}</span>
              </span>
              <span className="adm-log-time">{when(event.at)}</span>
            </li>
          ))}
        </ul>
      ) : <p className="adm-card adm-empty">Пока ничего нет.</p>}
    </>
  );
}
